import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTask, listLabels, getGoalTree, completeTask, type Task } from '../api';
import { QueryState } from './QueryState';
import { ParentLink } from './ParentLink';
import { BlockedBanner } from './BlockedBanner';
import { WaitingBanner, WaitingOnField } from './WaitingOn';
import { Links } from './Links';
import { Subtasks, SubtaskOffer } from './Subtasks';
import { AssigneeLine } from './AssigneeChip';
import { renderMarkdown } from '../markdown';
import { Checklist, Attachments, Thread } from './TaskDetail';
import { FailureNote } from './FailureNote';
import { useProjectRole } from '../roles';
import {
  STATUS_LOOK,
  STATUS_VERB,
  PRIORITY_LABELS,
  displayStatus,
  dueLook,
  dueClass,
  dueTitle,
} from '../taskVocab';
import { useAccountDay } from '../accountDay';
import { isTypingTarget } from '../keyboard';
import { hasOpenSurface } from '../useDismiss';
import { useKeys } from '../keyScope';
import { taskOpener, useFocusOnOpen } from '../useFocusOnOpen';
import { useRef } from 'react';

/**
 * A task, to read.
 *
 * Every route into a task landed in the side panel, and that panel is an edit form from top to
 * bottom — `MarkdownEditor` opens with its preview off, so a description was a textarea of raw
 * Markdown in a 384px column, below three sections of facet prose and below the fold. A task
 * carrying a table or a list of conditions could not be read at all without clicking Preview,
 * which does not persist.
 *
 * So this is the read half: the description rendered, and rendered first, with the facets
 * compressed into a line of pills above it and the attached things given the width to be useful.
 * Editing is one press away and unchanged — the panel is still where a task is written.
 *
 * It is deliberately not a second implementation of the panel. Checklist, Attachments and Thread
 * are the panel's own components, imported; only the layout above them is new.
 */
function Facets({
  task,
  goalNames,
  labelNames,
}: {
  task: Task;
  goalNames: ReadonlyMap<string, string>;
  labelNames: ReadonlyMap<string, string>;
}) {
  const look = STATUS_LOOK[displayStatus(task)];
  /* The account's calendar, not the laptop's — the same answer the row this was opened from gives. */
  const { timezone, today } = useAccountDay();
  const due = dueLook(task, today, timezone);
  return (
    <div className="task-view-facets">
      <span className={`pill ${look.tone === 'muted' ? '' : look.tone}`}>{look.label}</span>
      {task.priority > 0 && <span className="pill">{PRIORITY_LABELS[task.priority]}</span>}
      {task.complexityPoints !== null && <span className="pill">{task.complexityPoints} pts</span>}
      {task.estimateMinutes !== null && <span className="pill">{task.estimateMinutes}m</span>}
      {due && (
        <span className={dueClass(due)} title={dueTitle(due)}>
          {due.text}
        </span>
      )}
      {task.goalIds.map((id) => (
        <span className="pill" key={id}>
          {goalNames.get(id) ?? 'a goal'}
        </span>
      ))}
      {task.labelIds.map((id) => (
        <span className="pill" key={id}>
          {labelNames.get(id) ?? 'a label'}
        </span>
      ))}
    </div>
  );
}

export function TaskView({
  taskId,
  onBack,
  onEdit,
  onOpenTask,
}: {
  taskId: string;
  onBack: () => void;
  onEdit: () => void;
  onOpenTask: (id: string) => void;
}) {
  const task = useQuery({ queryKey: ['task', taskId], queryFn: () => getTask(taskId) });
  const labels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });

  /* The same completion the panel does, so reading a task and finishing it are one screen. */
  const queryClient = useQueryClient();
  const finish = useMutation({
    mutationFn: () => completeTask(taskId),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  /*
    What this viewer's role lets them press.

    Above the early return, which is the rules-of-hooks constraint the panel already documents;
    the id is read off `task.data` rather than a prop because a task carries its own project.

    Both controls are gated on `assign`, not `write`. Complete is a status change and a developer
    holding the task may make it; Edit opens the panel, and a developer can change status, labels,
    goals and the day it is scheduled for in there — so gating Edit at `write` would have shut the
    developer out of the only screen where they can do their own job.
  */
  const { can, why } = useProjectRole(task.data?.projectId ?? null);
  const mayProgress = can('assign');
  const whyNotProgress = why('assign');
  /*
    And the sentence, which covers the screen rather than the button.

    It was `why('assign')` too, so a *developer* — who may complete and edit — saw nothing, while
    the four sections below still offered a subtask, a step, a link and a file that their role
    could not produce. `write` is the strongest level anything on this screen needs, so this is
    non-null exactly when something here is refused, and `roleReason` keys on the role rather than
    the level, so the sentence is the same one either way. The tooltip above stays at `assign`,
    because putting a refusal on an enabled Complete would be the screen contradicting itself.
  */
  const whyNot = why('write');

  /*
    This screen's own keys — which it had none of, while `?` on it printed the task list's.

    The list is unmounted while this view holds the pane, so every key the panel advertised here
    was a no-op, `Esc` included: `Anywhere` promises it closes whatever is open and
    `← Back` was the only way out. Declaring the scope is the same call that binds them, so the
    panel now shows these three and nothing else — see `keyScope.ts`.

    `e` and `x` are gated on the same `assign` the two buttons are, rather than on being listed:
    a key that acts where the button beside it is disabled would be the panel telling the truth
    and the screen not.

    Escape stands down for anything open over the view — the same layering the list and the board
    use, so backing out of a menu does not also leave the task.
  */
  useKeys('task', (e: KeyboardEvent) => {
    if (isTypingTarget()) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'Escape') {
      if (e.defaultPrevented || hasOpenSurface()) return;
      return onBack();
    }
    if (!mayProgress) return;
    if (e.key === 'e') {
      e.preventDefault();
      return onEdit();
    }
    if (e.key === 'x') {
      if (task.data?.status === 'done') return;
      e.preventDefault();
      return finish.mutate();
    }
  });

  /*
    The panel's focus rule, for the view that stands in for it below the breakpoint.

    This replaces the list rather than sitting beside it, so the row that opened it is unmounted
    while it is up — which is why the return is looked up by id when Back or Escape closes it, not
    remembered. The section itself takes focus, as the panel's `<aside>` does and for the same
    reasons; see `TaskDetail`. One element in both states below, so the ref survives the load.
  */
  const surface = useRef<HTMLElement>(null);
  useFocusOnOpen(true, surface, { returnTo: () => taskOpener(taskId) });
  const sectionProps = {
    className: 'task-view',
    ref: surface,
    tabIndex: -1,
    'aria-label': task.data?.title ?? 'Task',
  } as const;

  if (!task.data) {
    return (
      <section {...sectionProps}>
        <button className="link" onClick={onBack}>
          ← Back
        </button>
        <QueryState query={task} what="this task" />
      </section>
    );
  }

  const t = task.data;
  const labelNames = new Map((labels.data ?? []).map((l) => [l.id, l.name]));
  const goalNames = new Map<string, string>();
  const walk = (nodes: { goal: { id: string; title: string }; children: typeof nodes }[]) => {
    for (const n of nodes) {
      goalNames.set(n.goal.id, n.goal.title);
      walk(n.children);
    }
  };
  walk((goals.data ?? []) as never);

  const description = t.descriptionMd.trim();

  return (
    <section {...sectionProps}>
      <div className="task-view-head">
        <button className="link" onClick={onBack}>
          ← Back
        </button>
        <span className="row gap">
          {/*
            Completing needs no scrolling here either.

            This view could not finish a task at all — the only Complete in the app was 942px down
            the panel, so reading a task and finishing it were different screens. Same control and
            same word as the panel, the selection bar and the row menu — from `STATUS_VERB`, which
            is what makes that claim true rather than a coincidence four files apart. It was not:
            the bar said `Done` and the row's own status box said `Finish`.
          */}
          <button
            type="button"
            className="detail-complete"
            onClick={() => finish.mutate()}
            disabled={t.status === 'done' || !mayProgress}
            {...(whyNotProgress ? { title: whyNotProgress } : {})}
          >
            {t.status === 'done' ? STATUS_VERB.done.past : STATUS_VERB.done.verb}
          </button>
          {/* The panel is still where a task is written; this only takes over the reading. */}
          <button
            type="button"
            onClick={onEdit}
            disabled={!mayProgress}
            {...(whyNotProgress ? { title: whyNotProgress } : {})}
          >
            Edit
          </button>
        </span>
      </div>

      {/*
        The reason, in words, beside the controls it explains.

        A tooltip alone is not it: it needs a pointer, it needs a guess that hovering will say
        something, and on a phone it does not exist. The sentence is what makes a greyed button
        different from a broken one.
      */}
      {whyNot && (
        <p className="subtext" data-testid="role-note">
          {whyNot}
        </p>
      )}
      {/*
        And the refusal that got through anyway. Membership is cached for a minute, so a role
        changed under an open page leaves the screen offering something the server will refuse —
        the case no amount of care above can cover.
      */}
      <FailureNote error={finish.error} />

      <h2>{t.title}</h2>
      {/* Same as the panel: on a full-width screen the name is always drawn. */}
      {t.parent && (
        <p className="detail-parent">
          <ParentLink parent={t.parent} />
        </p>
      )}
      <Facets task={t} goalNames={goalNames} labelNames={labelNames} />

      {/* Who has it, on the screen where the question is actually asked. */}
      <AssigneeLine task={t} />

      {/*
        The other way a task can be off your plate: somebody outside the app has it.

        Directly under "Held by", because they answer the same question — who is expected to act
        next — at the same `assign` rung, and reading them together is how a panel says "assigned
        to you, and you are waiting on Acme" rather than leaving that in two places.

        Deliberately *not* beside `BlockedBanner` below, which prints "Blocked by <task title>"
        about an open blocker. The two sentences stacked would be one word doing two jobs an inch
        apart; separated, and with the party's name in this one, they read as the different facts
        they are.
      */}
      <WaitingBanner task={t} />
      <WaitingOnField task={t} />

      <SubtaskOffer task={t} />

      {/* The panel's banner, not a second generic one: the fix landed there and this view, added
          afterwards, printed "Blocked by an open task" while the app knew which task. */}
      <BlockedBanner task={t} onOpenTask={onOpenTask} />

      {/*
        First, and rendered. This is the whole point of the screen: a description with a table or a
        list of conditions was previously readable only as Markdown source.
      */}
      {description === '' ? (
        <p className="muted">No description.</p>
      ) : (
        <div className="markdown-preview task-view-body">{renderMarkdown(t.descriptionMd)}</div>
      )}

      {/* The same section the panel shows, from the same component — this screen drew its own
          version, which listed titles and nothing else and could not create one. */}
      <Subtasks taskId={taskId} projectId={t.projectId} parent={t} onOpenTask={onOpenTask} />

      <Checklist taskId={taskId} projectId={t.projectId} />

      {/* The same section the panel shows. This view listed links and offered no way to add or
          remove one, so the deeper surface could do less than the shallower. */}
      <Links taskId={taskId} projectId={t.projectId} onOpenTask={onOpenTask} />

      <Attachments taskId={taskId} projectId={t.projectId} />
      <Thread taskId={taskId} />
    </section>
  );
}
